const DEFAULT_OPTIONS = {
    maxDimension: 2048,
    jpegQuality: 0.9,
};
const PNG_MIME = "image/png";
const JPEG_MIME = "image/jpeg";
const PASS_THROUGH_MIMES = new Set([PNG_MIME, JPEG_MIME]);
const HEIC_MIMES = new Set([
    "image/heic",
    "image/heif",
    "image/heic-sequence",
    "image/heif-sequence",
]);
const TIFF_MIMES = new Set(["image/tiff", "image/tif"]);
const SVG_MIMES = new Set(["image/svg+xml"]);
const NATIVE_CANVAS_MIMES = new Set([
    "image/webp",
    "image/bmp",
    "image/x-ms-bmp",
    "image/gif",
    "image/avif",
]);
const MIME_BY_EXTENSION = {
    ".png": PNG_MIME,
    ".jpg": JPEG_MIME,
    ".jpeg": JPEG_MIME,
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".bmp": "image/bmp",
    ".tif": "image/tiff",
    ".tiff": "image/tiff",
    ".svg": "image/svg+xml",
    ".heic": "image/heic",
    ".heif": "image/heif",
    ".avif": "image/avif",
};
const AVIF_BRANDS = new Set(["avif", "avis"]);
const HEIC_BRANDS = new Set([
    "heic",
    "heix",
    "hevc",
    "hevx",
    "heim",
    "heis",
    "mif1",
    "msf1",
]);
function normalizeMimeAlias(mimeType) {
    const normalized = (mimeType || "").toLowerCase().trim();
    if (normalized === "image/jpg")
        return JPEG_MIME;
    if (normalized === "image/tif")
        return "image/tiff";
    return normalized;
}
function createFailure(code, message, originalMimeType) {
    return {
        ok: false,
        code,
        message,
        originalMimeType: normalizeMimeAlias(originalMimeType) || "application/octet-stream",
    };
}
function getFileExtension(filename) {
    const index = filename.lastIndexOf(".");
    if (index < 0)
        return null;
    return filename.slice(index).toLowerCase();
}
function detectMimeFromMagicBytes(bytes) {
    if (bytes.length >= 8 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a) {
        return PNG_MIME;
    }
    if (bytes.length >= 3 &&
        bytes[0] === 0xff &&
        bytes[1] === 0xd8 &&
        bytes[2] === 0xff) {
        return JPEG_MIME;
    }
    if (bytes.length >= 6) {
        const gif = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4], bytes[5]);
        if (gif === "GIF87a" || gif === "GIF89a") {
            return "image/gif";
        }
    }
    if (bytes.length >= 12) {
        const riff = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
        const webp = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        if (riff === "RIFF" && webp === "WEBP") {
            return "image/webp";
        }
    }
    if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
        return "image/bmp";
    }
    if (bytes.length >= 4) {
        const littleTiff = bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00;
        const bigTiff = bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a;
        if (littleTiff || bigTiff) {
            return "image/tiff";
        }
    }
    if (bytes.length >= 16) {
        const boxType = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
        if (boxType === "ftyp") {
            for (let i = 8; i + 3 < Math.min(bytes.length, 44); i += 4) {
                const brand = String.fromCharCode(bytes[i], bytes[i + 1], bytes[i + 2], bytes[i + 3]).toLowerCase();
                if (AVIF_BRANDS.has(brand)) {
                    return "image/avif";
                }
                if (HEIC_BRANDS.has(brand)) {
                    return "image/heic";
                }
            }
        }
    }
    const textSnippet = new TextDecoder("utf-8")
        .decode(bytes.slice(0, Math.min(bytes.length, 512)))
        .toLowerCase()
        .trimStart();
    if (textSnippet.startsWith("<svg") || textSnippet.includes("<svg")) {
        return "image/svg+xml";
    }
    if (textSnippet.startsWith("<?xml") && textSnippet.includes("<svg")) {
        return "image/svg+xml";
    }
    return null;
}
async function detectOriginalMime(file) {
    const header = new Uint8Array(await file.slice(0, 512).arrayBuffer());
    const magicMime = detectMimeFromMagicBytes(header);
    if (magicMime) {
        return normalizeMimeAlias(magicMime);
    }
    if (file.type) {
        return normalizeMimeAlias(file.type);
    }
    const extension = getFileExtension(file.name || "");
    if (extension && MIME_BY_EXTENSION[extension]) {
        return MIME_BY_EXTENSION[extension];
    }
    return "application/octet-stream";
}
function parseDataUrl(dataUrl) {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/i);
    if (!match)
        return null;
    return {
        mimeType: normalizeMimeAlias(match[1]),
        base64: match[2],
    };
}
function computeTargetDimensions(width, height, maxDimension) {
    if (!width || !height) {
        return { width: 0, height: 0, scaled: false };
    }
    if (width <= maxDimension && height <= maxDimension) {
        return { width, height, scaled: false };
    }
    const ratio = Math.min(maxDimension / width, maxDimension / height);
    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio)),
        scaled: true,
    };
}
async function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error || new Error("Failed to read blob"));
        reader.readAsDataURL(blob);
    });
}
async function loadImage(source) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to decode image data"));
        image.src = source;
    });
}
function canvasToDataUrl(canvas, mimeType, jpegQuality) {
    if (mimeType === JPEG_MIME) {
        return canvas.toDataURL(JPEG_MIME, jpegQuality);
    }
    return canvas.toDataURL(PNG_MIME);
}
function createCanvas(width, height) {
    if (!width || !height)
        return null;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
}
function getPreferredOutputMime(originalMimeType) {
    return originalMimeType === JPEG_MIME ? JPEG_MIME : PNG_MIME;
}
async function normalizeDecodedDataUrl(decodedDataUrl, originalMimeType, options, forceConverted) {
    const parsed = parseDataUrl(decodedDataUrl);
    if (!parsed) {
        return createFailure("invalid_image_data", "Could not parse decoded image data.", originalMimeType);
    }
    const image = await loadImage(decodedDataUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) {
        return createFailure("invalid_image_data", "Image has invalid dimensions.", originalMimeType);
    }
    const target = computeTargetDimensions(width, height, options.maxDimension);
    if (!target.width || !target.height) {
        return createFailure("invalid_image_data", "Image dimensions are not valid.", originalMimeType);
    }
    const preferredOutputMime = getPreferredOutputMime(originalMimeType);
    const passthroughCandidate = normalizeMimeAlias(parsed.mimeType);
    const canPassthrough = !forceConverted &&
        !target.scaled &&
        PASS_THROUGH_MIMES.has(passthroughCandidate) &&
        passthroughCandidate === preferredOutputMime;
    if (canPassthrough) {
        return {
            ok: true,
            mimeType: preferredOutputMime,
            base64: parsed.base64,
            previewDataUrl: `data:${preferredOutputMime};base64,${parsed.base64}`,
            originalMimeType,
            converted: false,
            width: target.width,
            height: target.height,
        };
    }
    const canvas = createCanvas(target.width, target.height);
    if (!canvas) {
        return createFailure("conversion_failed", "Could not allocate image canvas.", originalMimeType);
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return createFailure("conversion_failed", "Could not initialize image rendering context.", originalMimeType);
    }
    ctx.drawImage(image, 0, 0, target.width, target.height);
    const normalizedDataUrl = canvasToDataUrl(canvas, preferredOutputMime, options.jpegQuality);
    const normalizedParts = parseDataUrl(normalizedDataUrl);
    if (!normalizedParts) {
        return createFailure("conversion_failed", "Could not encode normalized image.", originalMimeType);
    }
    return {
        ok: true,
        mimeType: preferredOutputMime,
        base64: normalizedParts.base64,
        previewDataUrl: `data:${preferredOutputMime};base64,${normalizedParts.base64}`,
        originalMimeType,
        converted: forceConverted || target.scaled || parsed.mimeType !== preferredOutputMime,
        width: target.width,
        height: target.height,
    };
}
async function decodeHeicToDataUrl(file) {
    const heicModule = await import("heic2any");
    const heic2any = heicModule.default || heicModule;
    const converted = await heic2any({
        blob: file,
        toType: PNG_MIME,
    });
    const outputBlob = Array.isArray(converted) ? converted[0] : converted;
    if (!(outputBlob instanceof Blob)) {
        throw new Error("HEIC conversion did not produce a Blob");
    }
    return blobToDataUrl(outputBlob);
}
async function decodeTiffToDataUrl(file) {
    const utifModule = await import("utif2");
    const UTIF = utifModule.default || utifModule;
    const arrayBuffer = await file.arrayBuffer();
    const ifds = UTIF.decode(arrayBuffer);
    if (!Array.isArray(ifds) || ifds.length === 0) {
        throw new Error("Could not decode TIFF structure");
    }
    const ifd = ifds[0];
    UTIF.decodeImage(arrayBuffer, ifd);
    const rgba = UTIF.toRGBA8(ifd);
    const width = Number(ifd.width || ifd.t256 || ifd["256"] || 0);
    const height = Number(ifd.height || ifd.t257 || ifd["257"] || 0);
    if (!width || !height) {
        throw new Error("Invalid TIFF dimensions");
    }
    const canvas = createCanvas(width, height);
    if (!canvas) {
        throw new Error("Could not allocate TIFF canvas");
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Could not initialize TIFF canvas context");
    }
    const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL(PNG_MIME);
}
function parseSvgDimensions(svgText) {
    const fallback = { width: 1024, height: 1024 };
    try {
        const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
        const svgEl = doc.documentElement;
        if (!svgEl || svgEl.nodeName.toLowerCase() !== "svg") {
            return fallback;
        }
        const parseLength = (raw) => {
            if (!raw)
                return null;
            const trimmed = raw.trim().toLowerCase();
            const match = trimmed.match(/^([0-9]*\.?[0-9]+)(px)?$/);
            if (!match)
                return null;
            const value = Number(match[1]);
            return Number.isFinite(value) && value > 0 ? value : null;
        };
        const widthAttr = parseLength(svgEl.getAttribute("width"));
        const heightAttr = parseLength(svgEl.getAttribute("height"));
        if (widthAttr && heightAttr) {
            return { width: widthAttr, height: heightAttr };
        }
        const viewBox = svgEl.getAttribute("viewBox");
        if (viewBox) {
            const parts = viewBox
                .trim()
                .split(/[\s,]+/)
                .map((value) => Number(value))
                .filter((value) => Number.isFinite(value));
            if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
                return { width: parts[2], height: parts[3] };
            }
        }
        if (widthAttr)
            return { width: widthAttr, height: widthAttr };
        if (heightAttr)
            return { width: heightAttr, height: heightAttr };
        return fallback;
    }
    catch {
        return fallback;
    }
}
async function rasterizeSvgToDataUrl(file, options) {
    const { Canvg } = await import("canvg");
    const svgText = await file.text();
    const rawSize = parseSvgDimensions(svgText);
    const target = computeTargetDimensions(rawSize.width, rawSize.height, options.maxDimension);
    const canvas = createCanvas(target.width || rawSize.width, target.height || rawSize.height);
    if (!canvas) {
        throw new Error("Could not allocate SVG canvas");
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Could not initialize SVG canvas context");
    }
    const renderer = Canvg.fromString(ctx, svgText, {
        ignoreAnimation: true,
        ignoreMouse: true,
        ignoreDimensions: true,
        ignoreClear: true,
    });
    await renderer.render();
    return canvas.toDataURL(PNG_MIME);
}
async function decodeViaNativeCanvasToDataUrl(file) {
    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await loadImage(objectUrl);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        const canvas = createCanvas(width, height);
        if (!canvas) {
            throw new Error("Could not allocate native decode canvas");
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Could not initialize native decode canvas context");
        }
        ctx.drawImage(image, 0, 0, width, height);
        return canvas.toDataURL(PNG_MIME);
    }
    finally {
        URL.revokeObjectURL(objectUrl);
    }
}
export async function normalizeForLlm(file, partialOptions) {
    const options = {
        ...DEFAULT_OPTIONS,
        ...(partialOptions || {}),
    };
    try {
        const originalMimeType = await detectOriginalMime(file);
        if (!originalMimeType.startsWith("image/")) {
            return createFailure("not_image", "File is not recognized as an image.", originalMimeType);
        }
        if (PASS_THROUGH_MIMES.has(originalMimeType)) {
            const dataUrl = await blobToDataUrl(file);
            return normalizeDecodedDataUrl(dataUrl, originalMimeType, options, false);
        }
        if (HEIC_MIMES.has(originalMimeType)) {
            try {
                const dataUrl = await decodeHeicToDataUrl(file);
                return normalizeDecodedDataUrl(dataUrl, originalMimeType, options, true);
            }
            catch {
                return createFailure("decode_failed", "Could not decode HEIC/HEIF image.", originalMimeType);
            }
        }
        if (TIFF_MIMES.has(originalMimeType)) {
            try {
                const dataUrl = await decodeTiffToDataUrl(file);
                return normalizeDecodedDataUrl(dataUrl, originalMimeType, options, true);
            }
            catch {
                return createFailure("decode_failed", "Could not decode TIFF image.", originalMimeType);
            }
        }
        if (SVG_MIMES.has(originalMimeType)) {
            try {
                const dataUrl = await rasterizeSvgToDataUrl(file, options);
                return normalizeDecodedDataUrl(dataUrl, originalMimeType, options, true);
            }
            catch {
                return createFailure("decode_failed", "Could not rasterize SVG image.", originalMimeType);
            }
        }
        if (NATIVE_CANVAS_MIMES.has(originalMimeType)) {
            try {
                const dataUrl = await decodeViaNativeCanvasToDataUrl(file);
                return normalizeDecodedDataUrl(dataUrl, originalMimeType, options, true);
            }
            catch {
                return createFailure("decode_failed", "Could not decode image in browser runtime.", originalMimeType);
            }
        }
        return createFailure("unsupported_format", `Unsupported image format: ${originalMimeType}`, originalMimeType);
    }
    catch {
        return createFailure("conversion_failed", "Unexpected error while normalizing image.", file.type || "application/octet-stream");
    }
}
